import { FIELD_CLASS, cx } from '@/components/ui';
import { toMoneyDisplay, toMoneyValue } from '@/lib/format';

/**
 * 금액 입력칸. 입력하는 동안 콤마가 붙는다.
 *
 * 🔴 type="number" 를 쓸 수 없다
 *   브라우저가 숫자 아닌 문자를 거부해서 '1,000' 을 넣으면 값이 빈 문자열이 된다.
 *   콤마를 보여주려면 type="text" 로 두고 우리가 포맷을 관리해야 한다.
 *
 *   대신 inputMode="numeric" 을 붙여 폰에서 숫자 키패드가 뜨게 한다.
 *   type="number" 를 쓰던 이유가 대개 이것인데, inputMode 로 충분하다.
 *
 * 🔴 화면에 보이는 값과 넘기는 값이 다르다
 *   화면  '1,000'
 *   전달  '1000'    ← onChange 가 주는 값
 *
 *   호출부는 콤마를 신경 쓰지 않는다. 받은 값을 그대로 서버로 보내면 된다.
 *   이 규칙이 한 곳에만 있어야 "어떤 화면은 콤마가 섞여 저장되는" 일이 안 생긴다.
 *
 * type="number" 를 버리면서 함께 사라지는 것
 *   min·max·step 속성이 동작하지 않는다. 위아래 화살표도 없어진다.
 *   범위 검사는 호출부가 값으로 한다 — 어차피 서버도 값으로 검사한다.
 *   화살표는 금액에서 쓸모가 적다. 10만원씩 올리려고 누를 사람은 없다.
 */
export function MoneyInput({
  value,
  onChange,
  placeholder,
  disabled = false,
  className,
  ...rest
}: {
  /** 콤마 없는 숫자 문자열. 빈 문자열이면 비어 있는 칸이다 */
  value: string;
  /** 콤마를 뺀 값으로 불린다 */
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  'aria-label'?: string;
  id?: string;
  name?: string;
}) {
  return (
    <div className="relative">
      <input
        {...rest}
        type="text"
        inputMode="numeric"
        // 브라우저 자동완성이 금액칸에 주소나 카드번호를 채우는 것을 막는다.
        autoComplete="off"
        className={cx(FIELD_CLASS, 'pr-8 text-right tabular', className)}
        value={toMoneyDisplay(value)}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(event) => onChange(toMoneyValue(event.target.value))}
      />

      {/*
        단위를 칸 안에 겹쳐 둔다. 값의 일부가 아니라 장식이다.
        input 의 value 에 '원' 을 넣으면 지우다가 그것까지 서버로 넘어간다.

        pointer-events-none 이 없으면 '원' 을 눌렀을 때 입력칸이 포커스되지 않는다.
      */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-fg-subtle"
      >
        원
      </span>
    </div>
  );
}
